<template>
  <div style="top: 40px">
    <div v-if="series" class="viewer-container">
      <!-- Zoom lives on the right button, so the browser menu has to stay out of
           the way. -->
      <div
        id="canvas"
        ref="canvas"
        class="relative"
        :style="{ minHeight }"
        @contextmenu.prevent
      >
        <ScrollIndicator
          v-if="stack"
          :value="stack.currentImageIdIndex"
          :image-ids="stack.imageIds"
          :viewport-height="getViewportHeight()"
          :excluded-indices="removedFrameIndices"
          @update:value="handleScrollUpdate"
        ></ScrollIndicator>
        <!-- Exclusion is easy to lose track of while scrolling a stack, so mark it
             on the image itself as well as in the gutter. Pointer-events off: the
             redaction tool draws through this. -->
        <div v-if="editable && currentImageRemoved" class="excluded-overlay">
          <div class="excluded-badge">Excluded — will not be exported</div>
        </div>
      </div>
      <div v-if="stack" class="flex flex-wrap items-center gap-1 my-1">
        <button
          v-for="tool in LEFT_TOOL_BUTTONS"
          :key="tool.name"
          class="btn btn-xs"
          :class="leftTool === tool.name && !redacting ? 'btn-primary' : ''"
          :disabled="redacting"
          :title="tool.title"
          @click="setLeftTool(tool.name)"
        >
          {{ tool.label }}
        </button>
        <span class="opacity-30 mx-1">|</span>
        <button
          class="btn btn-xs"
          title="Scale the image to fit the viewport and re-centre it"
          @click="fitImage"
        >
          Fit
        </button>
        <button
          class="btn btn-xs"
          title="Reset zoom, pan and window/level"
          @click="resetViewport"
        >
          Reset
        </button>
        <span class="text-xs opacity-50 ml-2">
          Right-drag zoom · middle-drag pan · wheel scroll
        </span>
      </div>
      <div v-if="stack">
        Image {{ stack.currentImageIdIndex + 1 }} of {{ stack.imageIds.length }}
      </div>
      <div v-if="editable" class="">
        <fieldset>
          <legend>Exclude images</legend>
          <div class="flex flex-wrap items-center gap-2">
            <button class="btn" @click="toggleImageRemoved">
              {{
                currentImageRemoved ? "Include image" : "Exclude current image"
              }}
            </button>
            <button
              v-if="removedFrameIndices.length > 0"
              class="btn"
              title="Put every excluded image in this series back into the export"
              @click="emit('includeAllImages')"
            >
              Include all
            </button>
            <span v-if="currentImageRemoved" class="badge badge-error badge-sm">
              Image excluded from export
            </span>
          </div>
          <div
            v-if="removedFrameIndices.length > 0"
            class="text-sm text-error mt-1"
          >
            {{ removedFrameIndices.length }} of {{ seriesFrameCount }} images in
            this series excluded from export.
          </div>
        </fieldset>
        <div v-if="currentImageQuarantined" class="text-warning">
          {{ currentImageQuarantined.reason }}
        </div>
        <fieldset class="my-4">
          <legend>Redact pixels</legend>

          <div class="flex flex-wrap gap-1">
            <template v-if="!redacting">
              <button
                :disabled="currentImageRemoved"
                class="btn btn-sm"
                @click="startRedacting"
              >
                Add image redactions
              </button>
              <button class="btn btn-sm" @click="undoRedaction">
                Undo all redactions
              </button>
            </template>
            <button
              v-if="redacting"
              class="btn btn-sm"
              @click="stopRedacting('series')"
            >
              <span v-if="hasRedactions">Apply to series</span>
              <span v-else>Cancel redaction</span>
            </button>
            <button
              v-if="redacting && hasRedactions && matchingSeriesCount > 0"
              class="btn btn-sm"
              :title="
                `These boxes are pixel coordinates, so they can only be applied to images of the same size. ${matchingSeriesCount} other series in this study are ${matrixLabel}.`
              "
              @click="stopRedacting('study')"
            >
              Apply to all {{ matchingSeriesCount + 1 }} series ({{
                matrixLabel
              }})
            </button>
            <button
              v-if="redacting && hasRedactions"
              class="btn btn-sm"
              @click="clearRedactionBoxes"
            >
              Clear boxes
            </button>
          </div>
          <div v-if="redacting" class="text-sm text-opacity-40 p-1 mt-1">
            Click and drag on the image to draw redaction boxes, then click
            "Apply to series" to remove pixel data from all images in the
            series.
          </div>
          <!-- Committed state, read from the redaction store the export consults —
               not from the editor's own box list, which can differ from it. -->
          <div
            v-if="committedRedaction.redacted > 0"
            class="text-sm p-1 mt-1 text-success"
          >
            Redaction applied to {{ committedRedaction.redacted }} of
            {{ committedRedaction.total }}
            {{ committedRedaction.total === 1 ? "image" : "images" }} in this
            series.
          </div>
        </fieldset>

        <fieldset class="my-4">
          <legend>Trim series</legend>

          <div class="flex">
            <div class="w-1/3">Trim from start:</div>
            <div class="flex-grow">
              <input
                type="range"
                min="0"
                :max="seriesFrameCount"
                v-model="seriesTrimStart"
                @input="onUpdateTrimStart"
              />
              {{ seriesTrimStart }}
            </div>
          </div>
          <div class="flex">
            <div class="w-1/3">Trim from end:</div>
            <div class="flex-grow">
              <input
                type="range"
                min="0"
                :max="seriesFrameCount"
                v-model="seriesTrimEnd"
                @input="onUpdateTrimEnd"
                style="direction: rtl"
              />
              {{ seriesTrimEnd }}
            </div>
          </div>
        </fieldset>
        <div>
          Series images viewed: {{ numImagesDisplayed }} / {{ numImagesTotal }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import {
  cornerstone,
  cornerstoneTools,
} from "../cornerstone/cornerstone-setup.js";
import * as instanceSource from "../cornerstone/instance-source";
import RectangleRedact from "../cornerstone/rectangle-redact-tool.js";
import Instance from "../types/Instance.js";
import Series from "../types/Series";
import selectFrames from "./selectFrames";
import ScrollIndicator from "./ScrollIndicator.vue";
import {
  seriesRedactionStatus,
  useRedactionVersion,
} from "../pixel-redaction";
import { formatMatrixKey, seriesMatrixKey } from "../display-sets";

const scrollToIndex = cornerstoneTools.import("util/scrollToIndex");

const props = withDefaults(
  defineProps<{
    series: Series;
    height?: string;
    editable?: boolean;
    removedInstanceUids?: Instance["uid"][];
    /**
     * How many other series in this study carry the same image matrix, and can
     * therefore take these rectangles verbatim. Resolved by the parent, which is
     * the one holding the case. See `seriesMatchingMatrix` in pixel-redaction.
     */
    matchingSeriesCount?: number;
  }>(),
  { editable: false, matchingSeriesCount: 0 }
);

/**
 * How wide an "apply" reaches: just this series, or every series in the study
 * sharing its image matrix. The parent resolves what "study" means — see
 * `seriesMatchingMatrix` in pixel-redaction.
 */
type RedactionScope = "series" | "study";

const emit = defineEmits<{
  (
    e: "updateRedactionBoxes",
    redactionBoxes: any[],
    scope?: RedactionScope
  ): void;
  (e: "toggleImageRemoved", currentInstanceUid: string): void;
  (e: "includeAllImages"): void;
  (e: "removeRedactions"): void;
}>();

/**
 * Tools bound to the left mouse button, one at a time.
 *
 * Pan and Zoom exist because fit-to-window is computed once per series, for the
 * first image: anything larger later in the stack is drawn oversized and clipped,
 * and a burned-in banner that falls off the top edge can't be redacted at all
 * (issue #102). Rather than refitting on every image — which would fight the
 * user's own zoom on every scroll — the viewer hands them the controls and a Fit
 * button.
 */
const LEFT_TOOL_BUTTONS = [
  { name: "StackScroll", label: "Scroll", title: "Drag to scroll the stack" },
  { name: "Pan", label: "Pan", title: "Drag to move the image" },
  { name: "Zoom", label: "Zoom", title: "Drag up/down to zoom" },
  { name: "Wwwc", label: "W/L", title: "Drag to adjust window/level" },
] as const;
type LeftTool = (typeof LEFT_TOOL_BUTTONS)[number]["name"];

const stack = ref<{ imageIds: string[]; currentImageIdIndex: number } | null>(
  null
);
const minHeight = props.height || "500px";
const redactionBoxes = ref<any[]>([]);
const redacting = ref(false);
const imageIdsRendered = ref<string[]>([]);
const canvas = ref<HTMLDivElement | null>(null);
const seriesTrimStart = ref(props.series.trimStart || 0);
const seriesTrimEnd = ref(props.series.trimEnd || 0);
const leftTool = ref<LeftTool>("StackScroll");

/** "800×600" for this series, or "" when its instances disagree / are unreadable. */
const matrixLabel = computed(() => {
  const key = seriesMatrixKey(props.series);
  return key ? formatMatrixKey(key) : "";
});

// Total frames across the series (multiframe instances contribute all their
// frames). Trim sliders operate on this flattened frame count, matching what
// the viewer scrolls through.
const seriesFrameCount = computed(() =>
  props.series.instances.reduce(
    (total, instance) => total + instance.frames.length,
    0
  )
);

// What the export will actually do to this series, read from the redaction store
// rather than from `redactionBoxes` (which is only the editor's handle state).
const redactionVersion = useRedactionVersion();
const committedRedaction = computed(() => {
  redactionVersion.value;
  return seriesRedactionStatus(props.series);
});

/**
 * Hand back boxes drawn in an open redaction session so the parent can commit them
 * on the user's behalf, and clear them here so they can't be committed twice.
 *
 * Only `stopRedacting` ("Apply redactions to series") used to commit, so drawing
 * boxes and then switching series — or going straight to "Apply image edits and
 * anonymize" — discarded them silently. The boxes render as half-black overlays
 * while being drawn, so the screen looked partly redacted while nothing had been
 * stored. Committing on the way out errs toward over-redacting, which is the safe
 * direction; dropping them errs toward exporting PHI.
 */
function takePendingRedactions(): any[] | null {
  if (!redacting.value || redactionBoxes.value.length === 0) return null;
  const pending = [...redactionBoxes.value];
  redacting.value = false;
  redactionBoxes.value = [];
  return pending;
}

defineExpose({
  canvas,
  takePendingRedactions,
});

function getViewportHeight() {
  if (canvas.value) {
    return canvas.value.clientHeight;
  }
  return 0;
}
function onMeasurementAdded(e: any) {
  if (e.detail.toolType === "RectangleRedact") {
    const { measurementData }: { measurementData: any } = e.detail;
    measurementData.csid = crypto.randomUUID();
    redactionBoxes.value.push(measurementData);
    copyToAllImages();
  }
}
function onMeasurementRemoved(e: any) {
  if (e.detail.toolType === "RectangleRedact") {
    const { measurementData } = e.detail;
    const { csid } = measurementData;
    redactionBoxes.value.splice(
      redactionBoxes.value.findIndex((b) => b.csid === csid),
      1
    );
    copyToAllImages();
  }
}
function copyToAllImages() {
  if (!stack.value) return false;
  // Copy redact rectangles to all imageIds
  const annotations: { [key: string]: any } = {};
  stack.value.imageIds.forEach((imageId) => {
    annotations[imageId] = {
      RectangleRedact: {
        // Don't just pass this.redactionBoxes array here, because then the object will be
        // modified by both cornerstone and vue, and will duplicate tool data
        data: [...redactionBoxes.value],
      },
    };
  });
  cornerstoneTools.globalImageIdSpecificToolStateManager.restoreToolState(
    annotations
  );
  return true;
}
function onImageRendered(eventData: any) {
  const { imageId } = eventData.detail.image;
  if (!imageIdsRendered.value.includes(imageId)) {
    imageIdsRendered.value.push(imageId);
  }
}

onMounted(() => {
  if (props.series.instances.length > 0) {
    loadSeries();
  }
  canvas.value?.addEventListener(
    cornerstoneTools.EVENTS.MEASUREMENT_ADDED,
    onMeasurementAdded
  );
  canvas.value?.addEventListener(
    cornerstoneTools.EVENTS.MEASUREMENT_REMOVED,
    onMeasurementRemoved
  );
  // Image loading events are bound to the cornerstone object, not the element
  // cornerstone.events.addEventListener(cornerstone.EVENTS.IMAGE_LOADED, onImageLoaded);
  canvas.value?.addEventListener(
    cornerstone.EVENTS.IMAGE_RENDERED,
    onImageRendered
  );
  if (Array.isArray(props.series.redactionBoxes)) {
    redactionBoxes.value = [...props.series.redactionBoxes];
  }
});

onUnmounted(() => {
  // Catch-all for teardowns the parent doesn't drive (e.g. leaving the tab, which
  // unmounts the whole editor): commit anything still drawn rather than drop it.
  // The series-switch case is consumed by CaseEditor's pre-flush watcher before we
  // get here — it has to be, because by unmount time `props.series` already points
  // at the newly selected series.
  const pending = takePendingRedactions();
  if (pending) emit("updateRedactionBoxes", pending);
  // Release the parsed file the viewer was scrolling through. Not a
  // dataSetCacheManager.purge(): purge resets its maps without zeroing its size
  // accounting and strands any in-flight load, whereas releaseAll unwinds the
  // refcounts we actually took.
  instanceSource.releaseAll();
  // Managing the cache manually since we're directly editing files
  // cornerstone.imageCache.purgeCache();
  // clear all tool data
  cornerstoneTools.globalImageIdSpecificToolStateManager.restoreToolState({});
  // cornerstone.events.removeEventListener(cornerstone.EVENTS.IMAGE_LOADED, this.onImageLoaded);
  canvas.value?.removeEventListener(
    cornerstone.EVENTS.IMAGE_RENDERED,
    onImageRendered
  );
});

/**
 * Enable the element for cornerstone, once.
 *
 * `cornerstone.enable` doesn't dedupe: called again on an already-enabled element
 * it pushes a second entry and starts a second requestAnimationFrame draw loop,
 * while `getEnabledElement` keeps returning the first. Only `disable` clears them,
 * and nothing here calls it — so without this guard, dragging a trim slider (which
 * reloads the series on every `input` event) leaked a render loop per step.
 */
function ensureEnabled(element: HTMLElement) {
  try {
    cornerstone.getEnabledElement(element);
  } catch {
    cornerstone.enable(element);
  }
}

/**
 * Bind the mouse buttons for the current mode.
 *
 * Pan and Zoom hold the middle and right buttons at all times, redaction sessions
 * included — that's the point of the fix, since reaching a clipped banner is
 * exactly what the user needs to do *while* drawing a box. The left button goes to
 * whichever tool the toolbar has selected, or to the redaction tool while a
 * redaction session is open.
 *
 * No tool needs disabling by hand: cornerstone-tools resolves the collision
 * itself, stripping a mask from whichever tool held it when another claims it.
 */
function applyToolModes() {
  const element = canvas.value;
  if (!element) return;

  cornerstoneTools.setToolActiveForElement(element, "Pan", {
    mouseButtonMask: 4,
  });
  cornerstoneTools.setToolActiveForElement(element, "Zoom", {
    mouseButtonMask: 2,
  });

  if (!redacting.value) {
    // Enabled rather than active: the overlays still render, but the tool stops
    // creating new boxes.
    cornerstoneTools.setToolEnabledForElement(element, "RectangleRedact");
  }
  cornerstoneTools.setToolActiveForElement(
    element,
    redacting.value ? "RectangleRedact" : leftTool.value,
    { mouseButtonMask: 1, isTouchActive: true }
  );
}

function setLeftTool(name: LeftTool) {
  leftTool.value = name;
  applyToolModes();
}

/**
 * Scale the image to fit and re-centre it, leaving window/level alone.
 *
 * `fitToWindow` only touches the scale, so on its own it would leave a panned
 * image off-centre and still partly unreachable.
 */
function fitImage() {
  const element = canvas.value;
  if (!element) return;
  cornerstone.fitToWindow(element);
  const viewport = cornerstone.getViewport(element);
  viewport.translation.x = 0;
  viewport.translation.y = 0;
  cornerstone.setViewport(element, viewport);
}

/** Fit, centre, and restore the image's own window/level. */
function resetViewport() {
  const element = canvas.value;
  if (!element) return;
  cornerstone.reset(element);
}

function loadSeries(
  options: { startAt: "beginning" | "end" } = { startAt: "beginning" }
) {
  const element = canvas.value; // use built-in vue refs to access DOM element
  if (!element) return false;

  // Select kept frames at frame granularity (flatten instances → frames, then
  // apply trim) and build the imageId list for display. This is a
  // non-destructive preview; applyEdits() commits the same selection.
  const imageIds = selectFrames(props.series.instances, {
    trimStart: props.series.trimStart,
    trimEnd: props.series.trimEnd,
    includeInterval: props.series.includeInterval,
  }).flatMap(({ frames }) => frames.map((frame) => frame.imageId));

  // Nothing left to show (e.g. trimmed to zero frames) — bail before handing
  // cornerstone an undefined imageId.
  if (imageIds.length === 0) {
    stack.value = null;
    return false;
  }

  let currentImageIdIndex;
  if (options.startAt === "beginning") {
    currentImageIdIndex = 0;
  } else {
    currentImageIdIndex = imageIds.length - 1;
  }

  stack.value = {
    currentImageIdIndex,
    imageIds,
  };

  ensureEnabled(element);

  return cornerstone
    .loadImage(stack.value.imageIds[stack.value.currentImageIdIndex])
    .then((image: any) => {
      // display this image
      const viewport = cornerstone.getDefaultViewportForImage(element, image);

      cornerstone.invalidate(element);
      cornerstone.displayImage(element, image, viewport);

      // set the stack as tool state
      cornerstoneTools.clearToolState(element, "stack");
      cornerstoneTools.addStackStateManager(element, ["stack"]);
      cornerstoneTools.addToolState(element, "stack", stack.value);

      // Enable all tools we want to use with this element
      cornerstoneTools.addToolForElement(
        element,
        cornerstoneTools.StackScrollMouseWheelTool
      );
      cornerstoneTools.addToolForElement(
        element,
        cornerstoneTools.StackScrollTool
      );
      cornerstoneTools.addToolForElement(element, cornerstoneTools.WwwcTool);
      cornerstoneTools.addToolForElement(element, cornerstoneTools.PanTool);
      cornerstoneTools.addToolForElement(element, cornerstoneTools.ZoomTool);
      cornerstoneTools.addToolForElement(element, RectangleRedact);

      // Mousewheel stack scroll gets activated no matter what
      cornerstoneTools.setToolActiveForElement(
        element,
        "StackScrollMouseWheel",
        {
          mouseButtonMask: 0,
          isTouchActive: true,
        }
      );

      applyToolModes();

      // Only draw the box overlays while the user is actually editing. Once
      // redactions are applied, the pixels themselves are redacted, so drawing the
      // editing affordance (outline, handles, delete button) on top of them would
      // just be confusing. startRedacting restores them when editing resumes.
      if (redacting.value && redactionBoxes.value.length > 0) {
        copyToAllImages();
      }
    });
}

const numImagesDisplayed = computed(() => {
  if (!stack.value) return 0;
  return imageIdsRendered.value.length;
});
const numImagesTotal = computed(() => {
  if (!stack.value) return false;
  return stack.value.imageIds.length;
});
// const allImagesDisplayed = computed(() => {
//   if (!stack.value) return false;
//   return numImagesDisplayed.value >= numImagesTotal.value;
// });
const hasRedactions = computed(() => {
  return redactionBoxes.value && redactionBoxes.value.length > 0;
});
const currentImageId = computed(() => {
  if (!stack.value) return false;
  const currentImageId = stack.value.imageIds[stack.value.currentImageIdIndex];
  return currentImageId;
});
const currentInstance = computed(() => {
  // Find the instance that contains the current frame imageId
  return props.series.instances.find((instance) =>
    instance.frames.some((frame) => frame.imageId === currentImageId.value)
  );
});
const currentImageQuarantined = computed(() => {
  if (!stack.value) return false;
  return currentInstance.value?.quarantine;
});
const currentImageRemoved = computed(() => {
  if (!currentInstance.value) return false;
  return props.removedInstanceUids?.includes(currentInstance.value?.uid);
});
/**
 * Positions in the scrolled stack whose instance is excluded, so the gutter can
 * mark them. Frames are what the viewer scrolls, but exclusion is per instance —
 * every frame of a multiframe instance gets a mark.
 */
const removedFrameIndices = computed<number[]>(() => {
  if (!stack.value) return [];
  const removed = new Set(props.removedInstanceUids || []);
  if (removed.size === 0) return [];
  const removedImageIds = new Set(
    props.series.instances
      .filter((instance) => removed.has(instance.uid))
      .flatMap((instance) => instance.frames.map((frame) => frame.imageId))
  );
  if (removedImageIds.size === 0) return [];
  const indices: number[] = [];
  stack.value.imageIds.forEach((imageId, index) => {
    if (removedImageIds.has(imageId)) indices.push(index);
  });
  return indices;
});
// const currentImageUserRemoved = computed(() => {
//   if (
//     props.series.userRemoved &&
//     props.series.userRemoved.find((f) => f.imageId === currentImageId.value)
//   ) {
//     return true;
//   }
//   return false;
// });

function startRedacting() {
  redacting.value = true;
  const element = canvas.value;
  applyToolModes();
  // Bring back the boxes from a previous editing pass so they can be adjusted
  // rather than redrawn from scratch.
  if (redactionBoxes.value.length > 0) {
    copyToAllImages();
    cornerstone.updateImage(element);
  }
}

function stopRedacting(scope: RedactionScope = "series") {
  redacting.value = false;
  applyToolModes();

  emit("updateRedactionBoxes", redactionBoxes.value, scope);
}

function clearRedactionBoxes() {
  redactionBoxes.value = [];
  copyToAllImages();
  const element = canvas.value; // use built-in vue refs to access DOM element
  cornerstone.updateImage(element);
}

function undoRedaction() {
  clearRedactionBoxes();
  emit("removeRedactions");
}

function onUpdateTrimStart(e: Event) {
  // emit('updateTrimStart', parseInt((e.currentTarget as HTMLInputElement).value, 10));
  props.series.trimStart = parseInt(
    (e.currentTarget as HTMLInputElement).value,
    10
  );
  loadSeries();
}

function onUpdateTrimEnd(e: Event) {
  // emit('updateTrimEnd', parseInt((e.currentTarget as HTMLInputElement).value, 10));
  props.series.trimEnd = parseInt(
    (e.currentTarget as HTMLInputElement).value,
    10
  );
  loadSeries({ startAt: "end" });
}

function toggleImageRemoved() {
  if (currentInstance.value) {
    emit("toggleImageRemoved", currentInstance.value?.uid);
  }
}

function handleScrollUpdate(newImageIndex: number) {
  scrollToIndex(canvas.value, newImageIndex);
}
</script>

<style scoped>
#canvas {
  flex: auto;
  height: 100%;
  min-width: 0;
}

.excluded-overlay {
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  border: 2px solid var(--color-error);
  background: repeating-linear-gradient(
    45deg,
    color-mix(in srgb, var(--color-error) 16%, transparent) 0 10px,
    transparent 10px 30px
  );
}
.excluded-badge {
  margin-top: 0.5rem;
  padding: 0.15rem 0.6rem;
  border-radius: 0.25rem;
  background: var(--color-error);
  color: var(--color-error-content);
  font-size: 0.8rem;
  font-weight: 600;
}

fieldset {
  border: 1px solid var(--color-warning);
  padding: 0.5rem;
}
legend {
  padding-left: 0.25rem;
  padding-right: 0.25rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-warning);
  opacity: 0.7;
}
</style>
